// src/features/media/components/CaptionsList.tsx

import { Table, Badge, ActionIcon, Stack, Text, ThemeIcon, Button, Tooltip, Paper } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash, IconSubtask, IconPlus } from '@tabler/icons-react';
import { langCodeToInfo } from '@lib2/model/common/language/Languages';
import { useDeleteTextTrack } from '../useTextTrackMutations';
import type { ITextTrackData } from '@/lib/types/Media';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  awaiting_ingest: { label: 'Waiting', color: 'gray' },
  ingesting: { label: 'Uploading', color: 'blue' },
  processing: { label: 'Processing', color: 'yellow' },
  ready: { label: 'Ready', color: 'green' },
  error: { label: 'Error', color: 'red' },
  deleted: { label: 'Deleted', color: 'gray' },
};

interface CaptionsListProps {
  textTracks: ITextTrackData[];
  mediaId: number;
  onAdd: () => void;
}

export function CaptionsList({ textTracks, mediaId, onAdd }: CaptionsListProps) {
  const deleteTextTrack = useDeleteTextTrack(mediaId);
  const sortedTracks = [...textTracks].sort((a, b) => a.label.localeCompare(b.label));

  const handleDelete = (track: ITextTrackData) => {
    const langInfo = langCodeToInfo.get(track.langCode);
    const displayLabel = langInfo ? langInfo.nativeLabel : track.label;

    modals.openConfirmModal({
      title: 'Delete Caption?',
      children: (
        <Text size="sm" c="dimmed">
          This will permanently delete the {displayLabel} caption.
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteTextTrack.mutateAsync(track.fileId);
          notifications.show({
            title: 'Caption Deleted',
            message: `${displayLabel} caption removed`,
            color: 'green',
          });
        } catch (err) {
          notifications.show({
            title: 'Delete Failed',
            message: err instanceof Error ? err.message : 'Could not delete caption',
            color: 'red',
          });
        }
      },
    });
  };

  if (sortedTracks.length === 0) {
    return (
      <Stack align="center" gap="md" py="xl">
        <ThemeIcon variant="light" size="xl" color="gray">
          <IconSubtask size={24} />
        </ThemeIcon>
        <Text fw={500} size="lg" c="dark.7">No captions added yet</Text>
        <Text size="sm" c="dimmed" maw={300} ta="center">
          Add captions for accessibility and multi-language support.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={onAdd}>
          Add Caption
        </Button>
      </Stack>
    );
  }

  return (
    <Paper withBorder style={{ overflow: 'clip' }}>
    <Table highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Language</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th w={60} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sortedTracks.map((track) => {
          const langInfo = langCodeToInfo.get(track.langCode);
          const status = STATUS_CONFIG[track.fileStatusType] ?? { label: track.fileStatusType, color: 'gray' };

          return (
            <Table.Tr key={track.fileId || `${track.langCode}`}>
              <Table.Td>
                {langInfo ? (
                  <>
                    {langInfo.nativeLabel}
                    {langInfo.nativeLabel !== langInfo.englishLabel && (
                      <Text span size="sm" c="dimmed"> ({langInfo.englishLabel})</Text>
                    )}
                  </>
                ) : (
                  track.label
                )}
              </Table.Td>
              <Table.Td>
                <Badge variant="light" color={status.color} size="sm">
                  {status.label}
                </Badge>
              </Table.Td>
              <Table.Td>
                {track.fileId > 0 && (
                  <Tooltip label="Delete caption" openDelay={400}>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDelete(track)}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
    </Paper>
  );
}
